import React, { useEffect, useState } from 'react'
import * as Styled from './Styled'
import TextFieldField from '../Fields/TextFieldField/TextFieldField'
import ButtonShadow from '../ButtonShadow/ButtonShadow'
import { Formik } from 'formik'
import * as Yup from 'yup'
import SelectField from '../Fields/SelectField/SelectField'
import { MenuItem } from '@material-ui/core'
import { updateUser } from '../../services/api'
import { refreshAvailableProfiles } from '../../reducers/profiles'
import { useDispatch } from 'react-redux'
import { useTypedSelector } from '../../reducers'
import { countries, countriesStates } from 'utils/countries'
import 'react-international-phone/style.css'
import PhoneField from 'components/Fields/PhoneField/PhoneField'
import { isValidPhoneNumber } from 'libphonenumber-js'

interface Props {
  onSubmitAddress: () => void
}

const createAddressValidationSchema = Yup.object().shape({
  phone: Yup.string()
    .required('Campo obrigatório')
    .test('phone', 'Telefone inválido', (value) =>
      isValidPhoneNumber(value ?? ''),
    ),
  zipCode: Yup.string()
    .required('Campo obrigatório')
    .min(3, 'Campo obrigatório'),
  streetAddress: Yup.string()
    .required('Campo obrigatório')
    .min(1, 'Campo obrigatório')
    .max(255),
  number: Yup.string()
    .required('Campo obrigatório')
    .min(1, 'Campo obrigatório')
    .max(255),
  complement: Yup.string().nullable(),
  state: Yup.string()
    .required('Campo obrigatório')
    .min(1, 'Campo obrigatório')
    .max(6),
  country: Yup.string()
    .required('Campo obrigatório')
    .min(1, 'Campo obrigatório')
    .max(6),
  city: Yup.string()
    .required('Campo obrigatório')
    .min(1, 'Campo obrigatório')
    .max(255),
})

interface FormValues {
  phone: string
  zipCode: string
  streetAddress: string
  number: string
  complement: string
  state: string
  city: string
  country: string
}

const Address = ({ onSubmitAddress }: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false)
  const reduxDispatch = useDispatch()

  const profile = useTypedSelector(
    (state) => state.profiles && state.profiles.currentProfile,
  )

  useEffect(() => {
    reduxDispatch(refreshAvailableProfiles())
  }, [])

  const submitForm = async (formValues: FormValues): Promise<void> => {
    setIsLoading(true)

    const body = {
      name: profile.name,
      phone: formValues.phone,
      address: {
        streetAddress: formValues.streetAddress,
        number: formValues.number,
        complement: formValues.complement,
        city: formValues.city,
        country: formValues.country,
        state: formValues.state,
        zipCode: formValues.zipCode,
      },
    }

    const res = await updateUser(body)
    if (res.error) {
      alert(
        'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte',
      )
    } else {
      await reduxDispatch(refreshAvailableProfiles())
      onSubmitAddress()
    }

    setIsLoading(false)
  }

  return (
    <div>
      <Styled.SupportText>Dados de cobrança</Styled.SupportText>
      <Formik
        initialValues={{
          phone: profile?.phone || '',
          streetAddress: profile?.streetAddress || '',
          number: profile?.number || '',
          complement: profile?.complement || '',
          city: profile?.city || '',
          country: profile?.country || 'BR',
          state: profile?.state || '',
          zipCode: profile?.zipCode || '',
        }}
        onSubmit={(values) => submitForm(values)}
        validationSchema={createAddressValidationSchema}
        enableReinitialize
      >
        {({ handleSubmit, errors, touched, values }) => {
          return (
            <Styled.FlexForm>
              <PhoneField name="phone" />
              <TextFieldField
                label="Código Postal"
                placeholder="Código Postal"
                name="zipCode"
                variant="outlined"
                error={!!(touched.zipCode && errors?.zipCode)}
                helperText={touched.zipCode && errors?.zipCode}
                fullWidth
                value={values.zipCode}
                darkMode
                style={{ fontSize: 16, fontWeight: 500 }}
              />
              <TextFieldField
                label="Endereço"
                placeholder="Endereço"
                name="streetAddress"
                variant="outlined"
                error={!!(touched.streetAddress && errors?.streetAddress)}
                helperText={touched.streetAddress && errors?.streetAddress}
                fullWidth
                value={values.streetAddress}
                darkMode
                style={{ fontSize: 16, fontWeight: 500 }}
              />
              <TextFieldField
                label="Número"
                placeholder="Número"
                name="number"
                variant="outlined"
                error={!!(touched.number && errors?.number)}
                helperText={touched.number && errors?.number}
                fullWidth
                value={values.number}
                darkMode
                style={{ fontSize: 16, fontWeight: 500 }}
              />
              <TextFieldField
                label="Complemento"
                placeholder="Complemento"
                name="complement"
                variant="outlined"
                error={!!(touched.complement && errors?.complement)}
                helperText={touched.complement && errors?.complement}
                fullWidth
                value={values.complement}
                darkMode
                style={{ fontSize: 16, fontWeight: 500 }}
              />
              <div>
                <SelectField
                  name="country"
                  label="País"
                  placeholder="País"
                  variant="outlined"
                  displayEmpty
                  error={!!(touched.country && errors?.country)}
                  helperText={touched.country && (errors?.country as string)}
                  value={values.country}
                  darkMode
                  fullWidth
                  style={{ fontSize: 16, fontWeight: 500 }}
                >
                  {Object.entries(countries).map(([code, name]) => (
                    <MenuItem value={code}>{name}</MenuItem>
                  ))}
                </SelectField>
              </div>
              <div>
                <SelectField
                  name="state"
                  label="Estado"
                  placeholder="Estado"
                  variant="outlined"
                  displayEmpty
                  error={!!(touched.state && errors?.state)}
                  helperText={touched.state && (errors?.state as string)}
                  value={values.state}
                  darkMode
                  fullWidth
                  style={{ fontSize: 16, fontWeight: 500 }}
                >
                  {Object.entries(countriesStates[values.country] ?? {}).map(
                    ([code, name]) => (
                      <MenuItem value={code}>{name}</MenuItem>
                    ),
                  )}
                </SelectField>
              </div>
              <TextFieldField
                label="Cidade"
                placeholder="Cidade"
                name="city"
                variant="outlined"
                error={!!(touched.city && errors?.city)}
                helperText={touched.city && errors?.city}
                fullWidth
                value={values.city}
                darkMode
                style={{ fontSize: 16, fontWeight: 500 }}
              />
              <ButtonShadow
                onPress={() => {
                  if (!isLoading) handleSubmit()
                }}
                label="Salvar"
                disabled={isLoading}
              />
            </Styled.FlexForm>
          )
        }}
      </Formik>
    </div>
  )
}

export default Address
